<template>
  <div class="about-page">

    <!-- Loading skeleton -->
    <div v-if="loading" class="container about-loading">
      <div class="skel skel-badge"></div>
      <div class="skel skel-line w-60"></div>
      <div class="skel skel-line w-90"></div>
      <div class="skel skel-hero"></div>
    </div>

    <!-- Nothing published yet -->
    <div v-else-if="!about || isAboutEmpty" class="container about-state">
      <i class="pi pi-info-circle"></i>
      <p>لا يوجد محتوى بعد.</p>
    </div>

    <template v-else>

      <!-- ══════════════ HERO ══════════════ -->
      <section class="a-hero" v-fade-up>
        <div class="a-hero-bg" aria-hidden="true">
          <div class="a-blob a-blob-1"></div>
          <div class="a-blob a-blob-2"></div>
        </div>

        <div class="section-container a-hero-inner">
          <div class="section-label">من نحن</div>
          <h1 class="a-hero-title">{{ about.title || 'من نحن' }}</h1>
          <p v-if="about.subtitle" class="a-hero-sub">{{ about.subtitle }}</p>

          <div class="a-hero-actions">
            <router-link to="/reservation" class="btn-primary">احجز الآن</router-link>
            <router-link to="/courses" class="btn-secondary">تصفح الكورسات</router-link>
          </div>
        </div>

        <div class="section-container" v-if="about.image_link">
          <div class="a-hero-banner">
            <img :src="about.image_link" :alt="about.title" loading="eager" />
          </div>
        </div>
      </section>

      <!-- ══════════════ FEATURE CARDS (from content) ══════════════ -->
      <section class="a-section" v-if="featureCards.length">
        <div class="section-container">
          <SectionHeader label="ماذا نقدم" title="لماذا تختار منصتنا؟" />
          <div class="a-features-grid">
            <div
              v-for="(f, i) in featureCards"
              :key="i"
              class="feature-card"
              v-fade-up="{ delay: i * 70 }"
            >
              <div class="fc-icon-wrap" :class="iconColorClass(i)">
                <i :class="f.icon"></i>
              </div>
              <h3>{{ f.title }}</h3>
              <p v-if="f.desc">{{ f.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Fallback: content couldn't be split into cards (no <li>/<p> structure) -->
      <section class="a-section" v-else-if="about.content">
        <div class="section-container about-content-fallback" v-fade-up v-html="about.content"></div>
      </section>

      <!-- ══════════════ VISION & MISSION ══════════════ -->
      <section class="a-section a-section--tinted" v-if="about.vision || about.mission">
        <div class="section-container">
          <SectionHeader label="رؤيتنا ورسالتنا" title="نحو تعليم أفضل للجميع" />
          <div class="a-vm-grid">
            <div v-if="about.vision" class="a-vm-card" v-fade-up>
              <div class="fc-icon-wrap fc-purple"><i class="pi pi-compass"></i></div>
              <h3>رؤيتنا</h3>
              <p>{{ about.vision }}</p>
            </div>
            <div v-if="about.mission" class="a-vm-card" v-fade-up="{ delay: 100 }">
              <div class="fc-icon-wrap fc-blue"><i class="pi pi-flag"></i></div>
              <h3>رسالتنا</h3>
              <p>{{ about.mission }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════ STATISTICS ══════════════ -->
      <section
        v-if="about.statistics && about.statistics.length"
        ref="statsRef"
        class="a-stats-section"
      >
        <div class="a-stats-bg" aria-hidden="true"></div>
        <div class="section-container">
          <div class="a-stats-grid">
            <div
              v-for="(stat, i) in about.statistics"
              :key="i"
              class="a-stat-card"
              v-fade-up="{ delay: i * 80 }"
            >
              <div class="a-stat-icon"><i :class="statIcon(i)"></i></div>
              <div class="a-stat-num">{{ statDisplay[i] ?? stat.value }}</div>
              <div class="a-stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════ GALLERY ══════════════ -->
      <section class="a-section" v-if="about.gallery_links && about.gallery_links.length">
        <div class="section-container">
          <SectionHeader label="معرض الصور" title="لمحة من رحلتنا" />
          <div class="a-gallery-grid">
            <div
              v-for="(link, i) in about.gallery_links"
              :key="link"
              class="a-gallery-item"
              v-fade-up="{ delay: (i % 6) * 60 }"
            >
              <img :src="link" alt="" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

    </template>
  </div>
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader.vue';
import { blogService } from '@/services/blogService.js';
import { applySeoMeta } from '@/composables/useSeoMeta';

// Rotates through the site's established feature-icon palette
// (see QuickAccessSection.vue's fc-purple/fc-blue/fc-green/fc-orange).
const ICON_COLORS = ['fc-purple', 'fc-blue', 'fc-green', 'fc-orange'];
const FEATURE_ICONS = [
  'pi pi-book', 'pi pi-video', 'pi pi-chart-line', 'pi pi-comments',
  'pi pi-check-circle', 'pi pi-desktop', 'pi pi-shield', 'pi pi-users',
];
const STAT_ICONS = ['pi pi-users', 'pi pi-calendar', 'pi pi-file-edit', 'pi pi-star', 'pi pi-book', 'pi pi-trophy'];

export default {
  name: 'AboutPage',
  components: { SectionHeader },

  // Lightweight scroll-reveal, no external animation library — mirrors the
  // IntersectionObserver technique already used by components/home/StatsSection.vue.
  directives: {
    fadeUp: {
      mounted(el, binding) {
        el.classList.add('fade-up-init');
        const delay = binding.value?.delay || 0;
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => el.classList.add('fade-up-visible'), delay);
              observer.disconnect();
            }
          });
        }, { threshold: 0.15 });
        observer.observe(el);
      },
    },
  },

  data() {
    return {
      about: null,
      loading: true,
      statDisplay: [],
    };
  },

  computed: {
    isAboutEmpty() {
      const a = this.about;
      if (!a) return true;
      return !a.title && !a.subtitle && !a.content && !a.image_link
        && !a.vision && !a.mission
        && !(a.statistics && a.statistics.length)
        && !(a.gallery_links && a.gallery_links.length);
    },

    // Splits the existing free-form `content` HTML into short feature cards
    // instead of one long article block — no new data field, same source of
    // truth. Prefers <li> items (a bullet list of short features/services);
    // falls back to <p> paragraphs (title = first few words, desc = full text)
    // when the content has no list markup.
    featureCards() {
      const html = this.about?.content;
      if (!html) return [];

      const doc = new DOMParser().parseFromString(html, 'text/html');

      const liItems = Array.from(doc.querySelectorAll('li'))
        .map((li) => li.textContent.trim())
        .filter(Boolean);

      if (liItems.length) {
        return liItems.slice(0, 8).map((text, i) => ({
          title: text,
          desc: '',
          icon: FEATURE_ICONS[i % FEATURE_ICONS.length],
        }));
      }

      const paragraphs = Array.from(doc.querySelectorAll('p'))
        .map((p) => p.textContent.trim())
        .filter(Boolean);

      return paragraphs.slice(0, 6).map((text, i) => ({
        title: text.split(/\s+/).slice(0, 4).join(' '),
        desc: text,
        icon: FEATURE_ICONS[i % FEATURE_ICONS.length],
      }));
    },
  },

  methods: {
    iconColorClass(i) {
      return ICON_COLORS[i % ICON_COLORS.length];
    },
    statIcon(i) {
      return STAT_ICONS[i % STAT_ICONS.length];
    },

    // "+5000" -> prefix "+", target 5000, suffix ""; "95%" -> prefix "", target 95, suffix "%".
    parseStatValue(raw) {
      const str = String(raw ?? '').trim();
      const match = str.match(/^(\D*)(\d+)(\D*)$/);
      if (!match) return { prefix: '', target: null, suffix: str };
      const [, prefix, digits, suffix] = match;
      return { prefix, target: parseInt(digits, 10), suffix };
    },

    animateStats() {
      (this.about.statistics || []).forEach((stat, i) => {
        const { prefix, target, suffix } = this.parseStatValue(stat.value);
        if (target === null) {
          this.statDisplay[i] = stat.value;
          return;
        }
        this.statDisplay[i] = `${prefix}0${suffix}`;
        const duration = 1200;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.round(target * eased);
          this.statDisplay[i] = `${prefix}${current}${suffix}`;
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    },

    watchStatsSection() {
      const el = this.$refs.statsRef;
      if (!el) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.animateStats();
            observer.disconnect();
          }
        });
      }, { threshold: 0.3 });
      observer.observe(el);
    },
  },

  async mounted() {
    try {
      this.about = await blogService.aboutPage();
    } finally {
      this.loading = false;
    }

    this._seoCleanup = applySeoMeta({
      title: `${this.about?.meta_title || 'من نحن'} | أ/ طارق عبدالغني عبدالباري - مدرس الرياضيات`,
      description: this.about?.meta_description || 'تعرّف على أ/ طارق عبدالغني عبدالباري ومنصته التعليمية لتدريس الرياضيات.',
      canonical: 'https://tarekmath.com/about',
      image: this.about?.og_image || undefined,
      type: 'article',
    });

    if (this.about?.statistics?.length) {
      await this.$nextTick();
      this.watchStatsSection();
    }
  },

  beforeUnmount() {
    if (this._seoCleanup) this._seoCleanup();
  },
};
</script>

<style scoped>
* , *::before, *::after { box-sizing: border-box; }
.about-page { font-family: 'Cairo', sans-serif; direction: rtl; overflow-x: hidden; color: #111827; }

.section-container { max-width: 1200px; margin: 0 auto; padding: 0 2rem; }
@media (max-width: 600px) { .section-container { padding: 0 1.25rem; } }

/* Shared badge/title (same look as components/common/SectionHeader.vue) */
.section-label {
  display: inline-block; background: rgba(91,95,239,.08);
  color: #5B5FEF; border: 1px solid rgba(91,95,239,.2);
  border-radius: 99px; padding: .3rem 1rem;
  font-size: .8rem; font-weight: 700; margin-bottom: 1rem;
}

/* Shared CTA buttons (same gradients as components/home/HeroSection.vue / CtaSection.vue) */
.btn-primary, .btn-secondary {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem;
  padding: .9rem 2.1rem; border-radius: 99px;
  font-weight: 700; font-size: 1rem; text-decoration: none;
  transition: all .3s cubic-bezier(.4,0,.2,1); border: none; cursor: pointer;
  font-family: 'Cairo', sans-serif;
}
.btn-primary { background: linear-gradient(135deg,#5B5FEF,#7C4DFF); color: #fff; box-shadow: 0 12px 32px rgba(91,95,239,.3); }
.btn-primary:hover { transform: translateY(-3px); box-shadow: 0 20px 50px rgba(91,95,239,.45); color: #fff; }
.btn-secondary { background: transparent; color: #5B5FEF; border: 2px solid rgba(91,95,239,.25) !important; }
.btn-secondary:hover { background: rgba(91,95,239,.06); border-color: #5B5FEF !important; transform: translateY(-2px); color: #5B5FEF; }

/* ── Fade-up-on-scroll (subtle, single transition, no JS animation loop) ── */
.fade-up-init { opacity: 0; transform: translateY(24px); transition: opacity .6s ease, transform .6s cubic-bezier(.4,0,.2,1); }
.fade-up-visible { opacity: 1; transform: translateY(0); }

/* ══════════════ Loading / empty states ══════════════ */
.about-loading { padding: 6rem 1rem 2rem; max-width: 780px; }
.skel { background: #e9edf5; border-radius: 8px; position: relative; overflow: hidden; margin-bottom: 1rem; }
.skel::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
  animation: skel-shimmer 1.3s infinite;
}
@keyframes skel-shimmer { 100% { transform: translateX(100%); } }
.skel-badge { width: 110px; height: 26px; border-radius: 99px; }
.skel-hero { aspect-ratio: 21/8; border-radius: 28px; margin-top: 1.5rem; }
.skel-line { height: 16px; }
.w-60 { width: 60%; } .w-90 { width: 90%; }

.about-state { text-align: center; padding: 7rem 1rem; color: #94a3b8; min-height: 50vh; }
.about-state .pi { font-size: 2.6rem; margin-bottom: .8rem; display: block; }

/* ══════════════ Hero ══════════════ */
.a-hero {
  position: relative;
  overflow: hidden;
  padding: 7rem 0 5rem;
  background: linear-gradient(180deg, #F5F7FF 0%, #ffffff 65%);
}
.a-hero-bg { position: absolute; inset: 0; pointer-events: none; }
.a-blob { position: absolute; border-radius: 50%; filter: blur(70px); opacity: .35; }
.a-blob-1 { width: 420px; height: 420px; background: radial-gradient(circle,#5B5FEF,#7C4DFF); top: -160px; right: -100px; }
.a-blob-2 { width: 360px; height: 360px; background: radial-gradient(circle,#00C2FF,#00D4A6); bottom: -140px; left: -120px; opacity: .25; }

.a-hero-inner { position: relative; z-index: 1; text-align: center; display: flex; flex-direction: column; align-items: center; }
.a-hero-title { font-size: clamp(2.2rem, 5vw, 3.4rem); font-weight: 900; line-height: 1.2; color: #111827; margin: 0 0 1rem; max-width: 800px; }
.a-hero-sub { font-size: 1.1rem; color: #6B7280; line-height: 1.8; max-width: 620px; margin: 0 0 2.2rem; }
.a-hero-actions { display: flex; gap: 1rem; flex-wrap: wrap; justify-content: center; }

.a-hero-banner {
  margin-top: 3.5rem; border-radius: 28px; overflow: hidden;
  box-shadow: 0 30px 70px rgba(17,24,39,.14);
  position: relative; z-index: 1;
}
.a-hero-banner img {
  width: 100%; aspect-ratio: 2.2 / 1; object-fit: cover; display: block;
  transition: transform .6s cubic-bezier(.4,0,.2,1);
}
.a-hero-banner:hover img { transform: scale(1.035); }

@media (max-width: 600px) {
  .a-hero { padding: 5.5rem 0 3rem; }
  .a-hero-actions { flex-direction: column; width: 100%; }
  .a-hero-actions .btn-primary, .a-hero-actions .btn-secondary { width: 100%; }
  .a-hero-banner img { aspect-ratio: 4 / 3; }
}

/* ══════════════ Generic section rhythm ══════════════ */
.a-section { padding: 5.5rem 0; }
.a-section--tinted { background: #F8F9FD; }
@media (max-width: 600px) { .a-section { padding: 3.5rem 0; } }

/* Fallback prose block (only used if content had no <li>/<p> to split) */
.about-content-fallback { line-height: 1.9; font-size: 1.02rem; color: #1F2937; max-width: 780px; margin: 0 auto; }
.about-content-fallback :deep(img) { max-width: 100%; border-radius: 12px; margin: 1rem 0; }

/* ══════════════ Feature cards (mirrors components/home/QuickAccessSection.vue) ══════════════ */
.a-features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.5rem;
  align-items: stretch; /* equal-height cards within each row, desktop + mobile */
}
.feature-card {
  background: #fff; border-radius: 20px; padding: 2rem 1.5rem;
  border: 1px solid rgba(91,95,239,.08); box-shadow: 0 4px 16px rgba(0,0,0,.05);
  transition: transform .35s cubic-bezier(.4,0,.2,1), box-shadow .35s;
  display: flex;
  flex-direction: column;
  gap: .85rem;
  /* min-height (not a fixed height) keeps every card visually the same size
     regardless of description length, while still letting an unusually long
     title wrap onto extra lines without being clipped. auto-fill rows are
     otherwise only stretched to match within their own row, not globally. */
  min-height: 280px;
  height: 100%;
  overflow: hidden; /* safety net: content can never visually escape the rounded card */
}
.feature-card:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(91,95,239,.12); border-color: rgba(91,95,239,.2); }
.feature-card h3 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.4;
  /* Title always wraps in full — never truncated/clamped. */
  word-break: normal;
  overflow-wrap: break-word;
  white-space: normal;
}
.feature-card p {
  font-size: .92rem;
  color: #6B7280;
  line-height: 1.7;
  margin: 0;
  flex-grow: 1; /* description fills the remaining card space so footers/icons stay aligned */
  min-height: 0; /* lets the flex item shrink below its content size instead of overflowing */
  word-break: normal;
  overflow-wrap: break-word;
  white-space: normal;
  /* Clamp to 3 lines only when the description is long enough to need it. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.fc-icon-wrap { width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; }
.fc-purple { background: linear-gradient(135deg, rgba(91,95,239,.1), rgba(124,77,255,.1)); color: #5B5FEF; }
.fc-blue   { background: linear-gradient(135deg, rgba(0,194,255,.1), rgba(0,102,255,.1)); color: #0066ff; }
.fc-green  { background: linear-gradient(135deg, rgba(0,212,166,.1), rgba(0,194,255,.1)); color: #00a884; }
.fc-orange { background: linear-gradient(135deg, rgba(245,158,11,.1), rgba(239,68,68,.1)); color: #ef4444; }

/* ══════════════ Vision & Mission ══════════════ */
.a-vm-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
@media (min-width: 768px) { .a-vm-grid { grid-template-columns: 1fr 1fr; } }
.a-vm-card {
  background: #fff; border-radius: 24px; padding: 2.5rem;
  border: 1px solid rgba(91,95,239,.08); box-shadow: 0 4px 20px rgba(0,0,0,.05);
  transition: transform .35s cubic-bezier(.4,0,.2,1), box-shadow .35s;
}
.a-vm-card:hover { transform: translateY(-6px); box-shadow: 0 24px 55px rgba(91,95,239,.14); }
.a-vm-card h3 { font-size: 1.3rem; font-weight: 800; color: #111827; margin: 1.1rem 0 .75rem; }
.a-vm-card p { font-size: 1rem; color: #4B5563; line-height: 1.9; margin: 0; }

/* ══════════════ Statistics ══════════════ */
.a-stats-section { padding: 5.5rem 0; position: relative; overflow: hidden; background: linear-gradient(135deg,#0f0c29,#302b63); }
.a-stats-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 50%, rgba(91,95,239,.2) 0%, transparent 70%); }
.a-stats-grid { position: relative; z-index: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1.5rem; }
.a-stat-card {
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.12);
  border-radius: 20px; padding: 2.2rem 1.25rem; text-align: center;
  backdrop-filter: blur(10px); transition: transform .3s;
}
.a-stat-card:hover { transform: translateY(-6px); }
.a-stat-icon { font-size: 1.6rem; color: #00D4A6; margin-bottom: .75rem; }
.a-stat-num { font-size: clamp(1.8rem, 3.5vw, 2.6rem); font-weight: 900; color: #fff; line-height: 1; font-family: 'Cairo', sans-serif; }
.a-stat-label { font-size: .92rem; color: rgba(255,255,255,.65); font-weight: 500; margin-top: .5rem; }

/* ══════════════ Gallery (pure-CSS responsive masonry) ══════════════ */
.a-gallery-grid { columns: 3 260px; column-gap: 1.25rem; }
.a-gallery-item { break-inside: avoid; margin-bottom: 1.25rem; border-radius: 18px; overflow: hidden; box-shadow: 0 6px 20px rgba(0,0,0,.08); }
.a-gallery-item img { width: 100%; display: block; transition: transform .5s cubic-bezier(.4,0,.2,1); }
.a-gallery-item:hover img { transform: scale(1.06); }
@media (max-width: 600px) { .a-gallery-grid { columns: 2 160px; column-gap: .85rem; } .a-gallery-item { margin-bottom: .85rem; } }
</style>
